import React, { useEffect, useState } from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { ICredential } from 'api/useCredentials';

import Input from 'components/Input/Input';

import { formatCredentialDate } from '../helpers/developerSettings.helpers';

export enum ECredentialValueRef {
  KEY = 'key',
  TOKEN = 'token'
}

interface IInvalidationCredentialModalProps {
  credential: ICredential | null;
  label: string;
  valueRef: ECredentialValueRef;
  isApproving: boolean;
  onCancel: () => void;
  onApprove: () => void;
}

export const InvalidationCredentialModal: React.FC<
  IInvalidationCredentialModalProps
> = ({ credential, valueRef, isApproving, onCancel, onApprove }) => {
  const [confirmation, setConfirmation] = useState('');
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const [isTokenCopied, setIsTokenCopied] = useState(false);

  useEffect(() => {
    setConfirmation('');
    setIsTokenVisible(false);
    setIsTokenCopied(false);
  }, []);

  if (!credential) {
    return null;
  }

  const handleCopyToken = (): void => {
    navigator.clipboard.writeText(credential.value);
    setIsTokenCopied(true);
    window.setTimeout(() => setIsTokenCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/50 backdrop-blur-sm">
      <div className="flex w-[483px] flex-col gap-6 rounded-lg border border-base-border bg-white p-6 shadow-xl">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-base-foreground">
            Invalidate {valueRef}
          </h2>
          <p className="text-sm text-base-mutedForeground">
            Invalidating this will immediately break integrations still using
            it. This action cannot be undone.
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-lg bg-gray-100 p-4">
          <CredentialDataRow
            label={valueRef}
            value={isTokenVisible ? credential.value : '••••••••••••••••'}
            actions={
              <>
                <FontAwesomeIcon
                  icon={
                    isTokenVisible
                      ? 'fa-regular fa-eye-slash'
                      : 'fa-regular fa-eye'
                  }
                  className="cursor-pointer text-base-mutedForeground"
                  onClick={() => setIsTokenVisible(!isTokenVisible)}
                />
                <FontAwesomeIcon
                  icon={
                    isTokenCopied
                      ? 'fa-regular fa-clipboard-check'
                      : 'fa-regular fa-copy'
                  }
                  className={`cursor-pointer ${isTokenCopied ? 'text-green-600' : 'text-base-mutedForeground'}`}
                  onClick={handleCopyToken}
                />
              </>
            }
          />
          <CredentialDataRow
            label="Created"
            value={formatCredentialDate(credential.updatedAt)}
          />
          <CredentialDataRow label="CreatedBy" value={credential.createdBy} />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-base-foreground">
            Paste the {valueRef} to confirm invalidation
          </label>
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder={`Paste ${valueRef} here`}
            inputClassName="placeholder:text-base-mutedForeground"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-base-foreground shadow-none"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onApprove}
            disabled={confirmation !== credential.value}
            isLoading={isApproving}
            className="bg-base-destructive text-base-primaryForeground hover:bg-button-hover-destructive disabled:opacity-50"
          >
            Invalidate
          </Button>
        </div>
      </div>
    </div>
  );
};

const CredentialDataRow: React.FC<{
  label: string;
  value: string;
  actions?: React.ReactNode;
}> = ({ label, value, actions }) => (
  <div className="grid grid-cols-[72px_minmax(0,1fr)_32px] gap-5 text-sm">
    <span className="text-gray-500 capitalize">{label}</span>
    <span className="min-w-0 truncate text-right font-medium text-base-mutedForeground">
      {value}
    </span>
    <span className="flex items-center justify-end gap-2">{actions}</span>
  </div>
);
